import { useState, useCallback } from "react";

/**
 * changePassword API の関数型
 */
export type ChangePasswordFunction = (params: {
  currentPassword: string;
  newPassword: string;
}) => Promise<{
  error?: { message?: string; code?: string } | null;
  data?: unknown;
}>;

/**
 * useChangePassword のオプション
 */
export interface UseChangePasswordOptions {
  /** changePassword API 関数（依存注入） */
  changePasswordApi: ChangePasswordFunction;
  /** パスワードの最小文字数（デフォルト: 8） */
  minPasswordLength?: number;
  /** パスワード変更成功時のコールバック */
  onSuccess?: () => void;
}

/**
 * パスワード変更の入力データ
 */
export interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * useChangePassword の戻り値
 */
export interface UseChangePasswordReturn {
  changePassword: (input: ChangePasswordInput) => Promise<{ success: boolean }>;
  isLoading: boolean;
  error: string | null;
  isSuccess: boolean;
  reset: () => void;
}

/**
 * パスワード変更のためのフック
 * バリデーションと API 呼び出しをアプリケーション層でカプセル化
 *
 * @example
 * ```tsx
 * import { changePassword as changePasswordApi } from "@/infrastructure/auth/auth-client";
 *
 * const { changePassword, isLoading, error, isSuccess, reset } = useChangePassword({
 *   changePasswordApi,
 *   minPasswordLength: 8,
 *   onSuccess: () => toast.success("パスワードを変更しました"),
 * });
 *
 * // フォームのサブミット時
 * const result = await changePassword({
 *   currentPassword: "oldPassword",
 *   newPassword: "newPassword123",
 *   confirmPassword: "newPassword123",
 * });
 * ```
 */
export function useChangePassword(options: UseChangePasswordOptions): UseChangePasswordReturn {
  const { changePasswordApi, minPasswordLength = 8, onSuccess } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const reset = useCallback(() => {
    setError(null);
    setIsSuccess(false);
  }, []);

  const changePassword = useCallback(
    async (input: ChangePasswordInput): Promise<{ success: boolean }> => {
      setError(null);
      setIsSuccess(false);

      // Validation
      if (input.newPassword !== input.confirmPassword) {
        setError("PASSWORD_MISMATCH");
        return { success: false };
      }

      if (input.newPassword.length < minPasswordLength) {
        setError("PASSWORD_TOO_SHORT");
        return { success: false };
      }

      setIsLoading(true);
      try {
        const result = await changePasswordApi({
          currentPassword: input.currentPassword,
          newPassword: input.newPassword,
        });

        if (result.error) {
          setError(result.error.message || "PASSWORD_CHANGE_FAILED");
          return { success: false };
        }

        setIsSuccess(true);
        onSuccess?.();
        return { success: true };
      } catch {
        setError("PASSWORD_CHANGE_FAILED");
        return { success: false };
      } finally {
        setIsLoading(false);
      }
    },
    [changePasswordApi, minPasswordLength, onSuccess]
  );

  return {
    changePassword,
    isLoading,
    error,
    isSuccess,
    reset,
  };
}
